import {
  Box,
  Button,
  Flex,
  Heading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react'
import { isEqual } from 'lodash-es'
import { DateTime } from 'luxon'
import { memo, type ReactNode, useState } from 'react'

import { useUser } from '@/client/context/user'
import * as url from '@/client/services/url'
import { KEY_CLUB_WORLD_SLUG, SHOW_COLLEGE_INFO_WORLD_SLUGS } from '@/client/services/worlds'

import ShwLink from '@/components/ShwLink'
import LevelFlair from '@/components/Worlds/UI/Level/LevelFlair'
import type { AugmentedWorldLevel } from '@/components/Worlds/UI/Level/types'

import type { UserDetails } from '../../types'
import AnniversaryIcon from '../Anniversary/AnniversaryIcon'
import Card from '../Card'
import Divider from '../Divider'
import Avatar from './Avatar'

type PopoverContentProps = {
  user: Pick<
    UserDetails,
    | 'id'
    | 'displayName'
    | 'avatarUrl'
    | 'pronouns'
    | 'bio'
    | 'collegeInfo'
    | 'username'
    | 'activeAnniversary'
    | 'roles'
    | 'keyClubDistrictName'
    | 'timezone'
  >
  worldSlug?: string | null
  worldLevel?: AugmentedWorldLevel | null
  omitCollegeInfo?: boolean
}

function PopoverContentInternal({
  user,
  worldSlug,
  worldLevel,
  omitCollegeInfo = false,
  currentTime,
}: { currentTime: DateTime } & PopoverContentProps): JSX.Element {
  const currentUser = useUser()

  const {
    displayName,
    id,
    pronouns,
    bio,
    username,
    activeAnniversary,
    keyClubDistrictName,
    timezone,
  } = user

  const isCurrentUser = id === currentUser.id

  return (
    <PopoverContent border="none">
      <Card width={{ base: '300px', md: '386px' }} boxShadow="0px 4px 4px 0px #00000040">
        <Stack direction="column" spacing="16px">
          <Flex justify="space-between" align="flex-start" width="100%">
            <Avatar user={user} size="lg" />
            <ShwLink href={url.getUserUrl({ userId: id, username })}>
              <Button>View Profile</Button>
            </ShwLink>
          </Flex>
          <Stack direction="column" spacing="8px">
            <Box>
              <Stack direction="row" align="center" spacing="8px">
                <Heading size="lg">{displayName}</Heading>
                {worldSlug === KEY_CLUB_WORLD_SLUG && keyClubDistrictName != null ? (
                  <Box ml="6px">
                    <Tag bg="cyan.subtle" color="cyan.fg" whiteSpace="nowrap">
                      {keyClubDistrictName}
                    </Tag>
                  </Box>
                ) : null}
                {activeAnniversary != null ? (
                  <AnniversaryIcon
                    years={activeAnniversary}
                    isCurrentUser={isCurrentUser}
                    displayName={displayName}
                    includeTooltip
                  />
                ) : null}
                {worldSlug == null && worldLevel == null ? null : (
                  <LevelFlair
                    user={user}
                    worldSlug={worldSlug ?? undefined}
                    level={worldLevel ?? null}
                  />
                )}
              </Stack>
              {username == null ? null : (
                <Text variant="supporting" color="fg.muted" fontSize="16px" mt="4px">
                  @{username}
                </Text>
              )}
            </Box>
            <Stack direction="row" spacing="4px">
              {pronouns == null ? null : (
                <Text variant="supporting" color="fg.muted">
                  {pronouns}
                </Text>
              )}
              {worldSlug != null &&
              SHOW_COLLEGE_INFO_WORLD_SLUGS.includes(worldSlug) &&
              !omitCollegeInfo &&
              user.collegeInfo != null ? (
                <>
                  <Text variant="supporting" color="fg.muted">
                    ·
                  </Text>
                  <Text variant="supporting" color="fg.muted" noOfLines={1}>
                    {user.collegeInfo.name}
                  </Text>
                </>
              ) : null}
              <Text variant="supporting" color="fg.muted">
                ·
              </Text>
              <Text variant="supporting" color="fg.muted">
                {currentTime.setZone(timezone).toLocaleString(DateTime.TIME_SIMPLE)} Local
              </Text>
            </Stack>
          </Stack>
          {bio.length === 0 ? null : (
            <>
              <Box flex={1}>
                <Divider my="0px" />
              </Box>
              <Text noOfLines={2}>{bio}</Text>
            </>
          )}
        </Stack>
      </Card>
    </PopoverContent>
  )
}

const PopoverContentMemoized = memo(PopoverContentInternal, isEqual)

function AvatarPopoverInternal({
  children,
  ...popoverContentProps
}: PopoverContentProps & { children: ReactNode }): JSX.Element {
  // We want the time displayed to remain up to date, so we compute it in this parent component and update it onOpen
  const [currentTime, setCurrentTime] = useState(() => DateTime.now().startOf('minute'))

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      openDelay={500}
      strategy="fixed"
      onOpen={() => setCurrentTime(DateTime.now().startOf('minute'))}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContentMemoized currentTime={currentTime} {...popoverContentProps} />
    </Popover>
  )
}

const AvatarPopover = memo(AvatarPopoverInternal, isEqual)

export default AvatarPopover
